import { Suspense, lazy } from 'react';
import { Navigate, RouteObject } from 'react-router';
import AuthGuard from './components/AuthGuard';
import RoleGuard from './components/RoleGuard';
import DashboardLayout from './components/dashboard/DashboardLayout';
import DashboardRedirect from './components/dashboard/DashboardRedirect';
import GuestGuard from './components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';
import OrderFlowLayout from './components/order/OrderFlowLayout';
import ItemDetailPage from './pages/detail/ItemDetailPage';
import OrderDetail from './pages/detail/OrderDetail';
import PerfectOrderDetail from './pages/perfectOrder/PerfectOrderDetail';
import WebUsers from './pages/webusers/WebUsers';
import Contacts from './pages/contacts/Contacts';
import PlantVolumes from './pages/plantVolumes/PlantVolumes';
import Activity from './pages/Activity/Activity';
import CustomerCartSummary from './components/myCustomers/CustomerCartSummary';
import CustomerShows from './components/myCustomers/CustomerShows';
import CustomerShowAttendees from './components/myCustomers/CustomerShowAttendees';
import CustomerContacts from './components/myCustomers/CustomerContacts';
import Reports from './pages/reports/Reports';
import CHRobinson from './pages/admin/CHRobinson';
import MaxDiscountRaw from './pages/admin/MaxDiscountRaw';
import MaxDiscountSummary from './pages/admin/MaxDiscountSummary';

const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Authentication pages
const Login = Loadable(lazy(() => import('./pages/authentication/Login')));

// Dashboard pages
const Overview = Loadable(lazy(() => import('./pages/dashboard/Overview')));
const ViewOrders = Loadable(lazy(() => import('./pages/sales/ViewOrders')));
const OrderEntry = Loadable(lazy(() => import('./pages/orderEntry/OrderEntry')));
const ViewOrder = Loadable(lazy(() => import('./pages/orderEntry/ViewOrder')));
const ItemEdit = Loadable(lazy(() => import('./pages/orderEntry/ItemEdit')));
const OrderHeader = Loadable(lazy(() => import('./pages/orderEntry/OrderHeader')));
const OrderSubmit = Loadable(lazy(() => import('./pages/orderEntry/OrderSubmit')));
const OrderChangeConfirmation = Loadable(lazy(() => import('./pages/orderEntry/OrderChangeConfirmation')));
const OrderConfirm = Loadable(lazy(() => import('./pages/orderEntry/OrderConfirm')));
const SettingsPage = Loadable(lazy(() => import('./pages/Settings')));
const CustomerDetails = Loadable(lazy(() => import('./pages/customer/CustomerDetails')));
const Analytics = Loadable(lazy(() => import('./pages/sales/Analytics')));
const PerfectOrders = Loadable(lazy(() => import('./pages/perfectOrder/PerfectOrders')));
const MyCustomers = Loadable(lazy(() => import('./pages/myCustomers/MyCustomers')));
const MaxDiscounts = Loadable(lazy(() => import('./pages/maxDiscounts/MaxDiscounts')));

// Error pages
const AuthorizationRequired = Loadable(lazy(() => import('./pages/AuthorizationRequired')));
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));

const routes: RouteObject[] = [
  {
    element: '',
    path: 'authentication',
    children: [
      {
        path: 'login',
        caseSensitive: false,
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      {
        path: 'login-unguarded',
        element: <Login />,
        caseSensitive: false,
      },
    ],
    caseSensitive: false,
  },
  {
    path: '/0',
    element: <Navigate to="/731422" replace />,
    caseSensitive: false,
  },
  {
    path: '',
    element: <MainLayout />,
    children: [
      {
        path: '/:customerNumber/*',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: '*',
            element: (
              <div className="overview">
                <Overview />
              </div>
            ),
            caseSensitive: false,
          },
          {
            path: 'CustomerDetails',
            element: <CustomerDetails />,
            caseSensitive: false,
          },
          // New layout route for the order flow
          {
            element: <OrderFlowLayout />,
            children: [
              {
                path: 'OrderEntry/:sfaOrderId',
                element: (
                  <div className="order-entry-view">
                    <Suspense fallback={<></>}>
                      <OrderEntry />
                    </Suspense>
                  </div>
                ),
                caseSensitive: false,
              },
              {
                path: 'ItemEdit/:sfaOrderId',
                element: (
                  <div className="item-edit-view">
                    <ItemEdit />
                  </div>
                ),
                caseSensitive: false,
              },
              {
                path: 'OrderHeader/:sfaOrderId',
                element: (
                  <div className="order-header-view">
                    <Suspense fallback={<></>}>
                      <OrderHeader />
                    </Suspense>
                  </div>
                ),
                caseSensitive: false,
              },
              {
                path: 'OrderSubmit/:sfaOrderId',
                element: (
                  <div className="order-submit">
                    <OrderSubmit />
                  </div>
                ),
                caseSensitive: false,
              },
              {
                path: 'OrderChangeConfirmation/:sfaOrderId',
                element: <OrderChangeConfirmation />,
                caseSensitive: false,
              },
              {
                path: 'OrderConfirm/:sfaOrderId',
                element: <OrderConfirm />,
                caseSensitive: false,
              },
            ],
          },
          {
            path: 'ViewOrder/:sfaOrderId',
            element: <ViewOrder />,
            caseSensitive: false,
          },
          {
            path: 'Contacts',
            element: <Contacts />,
            caseSensitive: false,
          },
          {
            path: 'WebUsers',
            element: <WebUsers />,
            caseSensitive: false,
          },
          {
            path: 'PlantVolumes',
            element: <PlantVolumes />,
            caseSensitive: false,
          },
          {
            path: 'OrderDetail/:orderNumber',
            element: <OrderDetail />,
            caseSensitive: false,
          },
          {
            path: 'ItemDetail/:itemNumber',
            element: <ItemDetailPage />,
            caseSensitive: false,
          },
          {
            path: 'Reports',
            element: <Reports />,
            caseSensitive: false,
          },
        ],
        caseSensitive: false,
      },
      {
        path: '/myCustomers',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: '/myCustomers/',
            element: <MyCustomers />,
            children: [
              {
                path: 'shoppingCarts',
                element: <CustomerCartSummary />,
                caseSensitive: false,
              },
              {
                path: 'shows',
                element: <CustomerShows />,
                caseSensitive: false,
              },
              {
                path: 'showAttendees',
                element: <CustomerShowAttendees />,
                caseSensitive: false,
              },
              {
                path: 'customerContacts',
                element: <CustomerContacts />,
                caseSensitive: false,
              },
            ],
            caseSensitive: false,
          },
        ],
        caseSensitive: false,
      },
      {
        path: '/activity',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: 'AllActivities',
            element: <Activity key="AllActivities" className="activity-grid-all" showCostomerColumn />,
            caseSensitive: false,
          },
          {
            path: 'Tasks',
            element: <Activity key="Tasks" className="activity-grid-tasks" showCostomerColumn defaultFilterByTask />,
            caseSensitive: false,
          },
        ],
      },
      {
        path: '/sales',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: 'ViewOrders/',
            element: (
              <div className="view-orders">
                <ViewOrders />
              </div>
            ),
            caseSensitive: false,
          },
          {
            path: 'Analytics/',
            element: (
              <div className="view-orders">
                <Analytics />
              </div>
            ),
            caseSensitive: false,
          },
          {
            path: 'MaxDiscounts/',
            element: <MaxDiscounts />,
            caseSensitive: false,
          },
          {
            path: 'PerfectOrders/',
            element: <PerfectOrders />,
            caseSensitive: false,
          },
        ],
      },
      {
        path: '/admin',
        element: (
          <AuthGuard>
            <RoleGuard allowedRoles={['Sales Manager']}>
              <DashboardLayout />
            </RoleGuard>
          </AuthGuard>
        ),
        children: [
          {
            path: 'chrobinson',
            element: <CHRobinson />,
            caseSensitive: false,
          },
          {
            path: 'maxdiscountraw',
            element: <MaxDiscountRaw />,
            caseSensitive: false,
          },
          {
            path: 'maxdiscountsummary',
            element: <MaxDiscountSummary />,
            caseSensitive: false,
          },
        ],
        caseSensitive: false,
      },
      {
        path: '/PerfectOrderDetail/:perfectOrderId',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: '',
            element: (
              <div className="overview">
                <PerfectOrderDetail />
              </div>
            ),
            caseSensitive: false,
          },
        ],
        caseSensitive: false,
      },
      {
        path: '/',
        element: (
          <AuthGuard>
            <DashboardRedirect />
          </AuthGuard>
        ),
        caseSensitive: false,
      },
      {
        path: '/Account',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: 'Settings/',
            element: <SettingsPage />,
            caseSensitive: false,
          },
        ],
        caseSensitive: false,
      },
      {
        path: '401',
        element: <AuthorizationRequired />,
        caseSensitive: false,
      },
      {
        path: '404',
        element: <NotFound />,
        caseSensitive: false,
      },
      {
        path: '500',
        element: <ServerError />,
        caseSensitive: false,
      },
      {
        path: '*',
        element: <NotFound />,
        caseSensitive: false,
      },
    ],
    caseSensitive: false,
  },
];

export default routes;
